import { IconPlus } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDropdownOptions } from '@/contexts/dropdown-options-context';
import { formatErrorMessage } from '@/utils/error-formatting';
import type { EntityConfig, FieldConfig } from './reference-admin-types';

interface AddReferenceAdminModalProps {
  config: EntityConfig;
  onSuccess?: (() => void) | undefined;
}

export const AddReferenceAdminModal: React.FC<AddReferenceAdminModalProps> = ({
  config,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currencyOptions, countryOptions, refetch: refetchDropdowns } = useDropdownOptions();

  // Initialize form data with default values
  const initialFormData = config.fields.reduce(
    (acc, field) => {
      acc[field.key] =
        field.defaultValue ??
        (field.type === 'checkbox' ? false : field.type === 'number' ? 0 : '');
      return acc;
    },
    {} as Record<string, unknown>
  );

  const [formData, setFormData] = useState(initialFormData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Transform data before sending
      const transformedData = { ...formData };

      // Convert string fields to uppercase where needed (code fields)
      config.fields.forEach((field) => {
        if (field.key.includes('code') && typeof transformedData[field.key] === 'string') {
          transformedData[field.key] = (transformedData[field.key] as string).toUpperCase();
        }
        // Convert empty strings to null for optional fields
        if (!field.required && transformedData[field.key] === '') {
          transformedData[field.key] = null;
        }
      });

      await config.createFn(transformedData);

      // Refetch dropdown options to include the newly created item
      refetchDropdowns();

      setOpen(false);
      setFormData(initialFormData);
      onSuccess?.();
    } catch (_error) {
      const errorMessage = formatErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (key: string, value: unknown) => {
    setFormData((prev: Record<string, unknown>) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setError(null);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset form data when modal is closed
      setFormData(initialFormData);
      setError(null);
    }
    setOpen(isOpen);
  };

  const renderField = (field: FieldConfig) => {
    const value = formData[field.key];

    switch (field.type) {
      case 'checkbox':
        return (
          <Checkbox
            id={field.key}
            checked={!!value}
            onCheckedChange={(checked) => updateFormData(field.key, !!checked)}
          />
        );

      case 'select': {
        // Get dynamic options based on field key
        let options = field.options || [];
        if (field.key === 'currency_code') {
          options = currencyOptions;
        } else if (field.key === 'country_code') {
          options = countryOptions;
        }

        return (
          <Select
            value={value?.toString() || ''}
            onValueChange={(newValue) => updateFormData(field.key, newValue)}
          >
            <SelectTrigger className="col-span-3">
              <SelectValue placeholder={field.placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      case 'number':
        return (
          <Input
            id={field.key}
            type="number"
            value={value as string | number}
            onChange={(e) => updateFormData(field.key, parseInt(e.target.value) || 0)}
            className="col-span-3"
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            required={field.required}
          />
        );

      default: // text
        return (
          <Input
            id={field.key}
            value={value as string | number}
            onChange={(e) => updateFormData(field.key, e.target.value)}
            className="col-span-3"
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            minLength={field.minLength}
            required={field.required}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="px-3">
          <IconPlus className="mr-2 h-3 w-3" />
          Add {config.singularName}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Add New {config.singularName}</DialogTitle>
          <DialogDescription>
            Create a new {config.singularName.toLowerCase()} entry. Fields marked with * are
            required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid gap-4 py-4">
            {config.fields.map((field) => (
              <div key={field.key} className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor={field.key} className="text-right">
                  {field.label} {field.required && '*'}
                </Label>
                {field.type === 'checkbox' ? (
                  renderField(field)
                ) : (
                  <div className="col-span-3">{renderField(field)}</div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : `Create ${config.singularName}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
